import "whatwg-fetch";
import { logUserOut } from "utils/helperFunctions";
import Cookies from "js-cookie";
import { store } from "../app";

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
async function parseJSON(response) {
  if (response.status === 204 || response.status === 202) {
    return response;
  }

  try {
    return await response.json();
  } catch (err) {
    // Return empty array if there is empty response
    return [];
  }
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
async function checkStatus(response) {
  const { status, url } = response;

  if (status >= 200 && status < 300) {
    return response;
  }

  // We do not logout for 401 response codes when:
  // - Accessing Dynamic Score Reports: 401 indicates invalid DSR token, not platform token
  // - Retrieving Lever integration from serverless api: 401 indicates invalid Lever token, not platform token
  // - Syncing jobs to Paylocity: 401 indicates invalid Paylocity token, not platform token
  // - Syncing jobs to iCIMS Prime: 401 indicates invalid iCIMS Prime token, not platform token
  const pathsToExcludeFromLogOut = [
    "webReports",
    "lever2.0/getIntegrationConfig",
    "paylocity/packages",
    "icimsprime/sync_packages",
    "orc/packages",
  ];

  const excludeFromLogout = pathsToExcludeFromLogOut.some((path) =>
    url.includes(path)
  );

  if (status === 401 && !excludeFromLogout) {
    /**
     * Sometimes the error will not cause the state to update so the logic in App/index.js's shouldComponentUpdate will not get executed.
     */
    setTimeout(() => {
      const state = store.getState();
      if (
        (Cookies.get().ssoLoginSuccess ||
          !state.get("App").get("showSessionExpirationModal")) &&
        localStorage.getItem("token")
      ) {
        logUserOut({ forceLogout: true });
      }
    }, 400);
  }

  const error = new Error(response);
  error.response = response;
  throw error;
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export default function request(
  url,
  options,
  explicitLoginToken = false,
  crossOrigin = false,
  contentType = false
) {
  const newOptions = { ...options };
  const accessToken = !explicitLoginToken
    ? localStorage.getItem("token")
    : explicitLoginToken;

  if (accessToken && !crossOrigin) {
    newOptions.headers = {
      Authorization: `${accessToken}`,
    };
  }

  if (contentType) {
    newOptions.headers = { ...newOptions.headers, "Content-Type": contentType };
  }

  return fetch(url, newOptions).then(checkStatus).then(parseJSON);
}

export const newLandingPageRequest = (url, options) => {
  const newOptions = { ...options };
  const accessToken = localStorage.getItem("token");

  if (!newOptions.headers) {
    newOptions.headers = {};
  }

  if (accessToken) {
    newOptions.headers.Authorization = accessToken;
  }

  newOptions.headers["Content-Type"] = "application/json";

  return fetch(url, newOptions).then(checkStatus).then(parseJSON);
};
